import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from 'Context/CartContext';
import * as S from './Header.style';
import LanguageToggle from '../LanguageToggle/LanguageToggle';
import CountrySelect from '../CountrySelect/CountrySelect';

const TAP_MOVEMENT_THRESHOLD_PX = 10;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { itemCount } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('mobile-menu-open');
      return () => {
        document.body.style.overflow = previous;
        document.body.classList.remove('mobile-menu-open');
      };
    }
  }, [menuOpen]);

  const go = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  // Toggle the mobile menu only on real taps – ignore taps that happened during a scroll
  // (i.e. when the finger moved more than the threshold between touchstart and touchend).
  const handleHamburgerTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    const t0 = e.touches[0];
    touchStartRef.current = { x: t0.clientX, y: t0.clientY, moved: false };
  };

  const handleHamburgerTouchMove = (e: React.TouchEvent<HTMLButtonElement>) => {
    const start = touchStartRef.current;
    if (!start) return;
    const t0 = e.touches[0];
    const dx = Math.abs(t0.clientX - start.x);
    const dy = Math.abs(t0.clientY - start.y);
    if (dx > TAP_MOVEMENT_THRESHOLD_PX || dy > TAP_MOVEMENT_THRESHOLD_PX) {
      start.moved = true;
    }
  };

  const handleHamburgerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // If a touch caused this click but the finger moved beyond the threshold,
    // treat it as a scroll/swipe gesture and suppress the toggle.
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (start && start.moved) {
      e.preventDefault();
      return;
    }
    setMenuOpen((v) => !v);
  };

  const navItems: Array<[string, string]> = [
    ['/kaufen', t('nav.buy')],
    ['/verkaufen', t('nav.sell')],
    ['/suchanfrage', t('nav.request')],
    ['/uber-mich', t('nav.about')],
    ['/showroom', t('nav.showroom')],
    ['/kontakt', t('nav.contact')],
  ];

  return (
    <S.HeaderContainer>
      <S.HeaderContent>
        <S.LogoContainer as={Link} to="/" onClick={() => setMenuOpen(false)} data-testid="header-logo">
          <S.Logo src="/assets/images/logo-header.png" alt="MR - luxury watches" />
          <S.BrandName>MR – luxury watches</S.BrandName>
        </S.LogoContainer>

        {/* Desktop Nav */}
        <S.Nav>
          {navItems.map(([path, label]) => (
            <S.NavLink
              key={path}
              as={Link}
              to={path}
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </S.NavLink>
          ))}
          <S.CartIconWrapper as={Link} to="/warenkorb" data-testid="header-cart-btn">
            <S.CartIcon />
            {itemCount > 0 && <S.CartBadge>{itemCount}</S.CartBadge>}
          </S.CartIconWrapper>
          <CountrySelect />
          <LanguageToggle />
        </S.Nav>

        {/* Mobile right cluster: cart + hamburger */}
        <S.MobileActions>
          <S.CartIconWrapper as={Link} to="/warenkorb" data-testid="header-cart-btn-mobile">
            <S.CartIcon />
            {itemCount > 0 && <S.CartBadge>{itemCount}</S.CartBadge>}
          </S.CartIconWrapper>
          <S.HamburgerButton
            onClick={handleHamburgerClick}
            onTouchStart={handleHamburgerTouchStart}
            onTouchMove={handleHamburgerTouchMove}
            aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={menuOpen}
            data-testid="header-hamburger-btn"
            open={menuOpen}
          >
            <span />
            <span />
            <span />
          </S.HamburgerButton>
        </S.MobileActions>
      </S.HeaderContent>

      {/* Mobile drawer */}
      <S.MobileBackdrop open={menuOpen} onClick={() => setMenuOpen(false)} />
      <S.MobileDrawer open={menuOpen} data-testid="mobile-menu" aria-hidden={!menuOpen}>
        <S.MobileNavList>
          {navItems.map(([path, label]) => (
            <S.MobileNavItem
              key={path}
              as={Link}
              to={path}
              onClick={() => setMenuOpen(false)}
              data-testid={`mobile-nav-${path}`}
            >
              {label}
            </S.MobileNavItem>
          ))}
        </S.MobileNavList>
        <S.MobileFooter>
          <CountrySelect dropUp />
          <LanguageToggle />
        </S.MobileFooter>
      </S.MobileDrawer>
    </S.HeaderContainer>
  );
};

export default Header;
